/**
 * Sound Effects Service
 *
 * Centralized sound management for the application
 * All sound effects are stored in /src/sound effects/
 */

// Import sound effect files
import inviteSentSound from '../sound effects/invitesent.mp3';
import leavingMeetingSound from '../sound effects/leavingmeeting.mp3';
import micMutedSound from '../sound effects/micmuted.mp3';
import newMessageSound from '../sound effects/newmessage.mp3';
import raisedHandSound from '../sound effects/raisedhand.mp3';
import recordingStartSound from '../sound effects/recordingstart.mp3';
import waitingRoomSound from '../sound effects/watingroom.mp3';

// Sound effect registry
const sounds = {
  inviteSent: new Audio(inviteSentSound),
  leavingMeeting: new Audio(leavingMeetingSound),
  micMuted: new Audio(micMutedSound),
  newMessage: new Audio(newMessageSound),
  raisedHand: new Audio(raisedHandSound),
  recordingStart: new Audio(recordingStartSound),
  waitingRoom: new Audio(waitingRoomSound),
};

// Set default volume for all sounds (0.0 to 1.0)
Object.values(sounds).forEach(sound => {
  sound.volume = 0.5; // 50% volume by default
});

/**
 * Play a sound effect
 * @param {string} soundName - Name of the sound to play
 * @param {number} volume - Optional volume override (0.0 to 1.0)
 */
export const playSound = (soundName, volume = null) => {
  const sound = sounds[soundName];

  if (!sound) {
    console.warn(`Sound effect "${soundName}" not found`);
    return;
  }

  try {
    // Clone the audio to allow overlapping plays
    const soundClone = sound.cloneNode();

    // Set volume if provided
    if (volume !== null) {
      soundClone.volume = Math.max(0, Math.min(1, volume));
    }

    // Play the sound
    const playPromise = soundClone.play();

    if (playPromise !== undefined) {
      playPromise.catch(error => {
        console.warn(`Failed to play sound "${soundName}":`, error);
      });
    }
  } catch (error) {
    console.warn(`Error playing sound "${soundName}":`, error);
  }
};

/**
 * Set global volume for all sounds
 * @param {number} volume - Volume level (0.0 to 1.0)
 */
export const setGlobalVolume = (volume) => {
  const clampedVolume = Math.max(0, Math.min(1, volume));
  Object.values(sounds).forEach(sound => {
    sound.volume = clampedVolume;
  });
};

/**
 * Preload all sounds (call this on app initialization)
 */
export const preloadSounds = () => {
  Object.values(sounds).forEach(sound => {
    sound.load();
  });
};

// Available sound effects
export const SOUNDS = {
  INVITE_SENT: 'inviteSent',
  LEAVING_MEETING: 'leavingMeeting',
  MIC_MUTED: 'micMuted',
  NEW_MESSAGE: 'newMessage',
  RAISED_HAND: 'raisedHand',
  RECORDING_START: 'recordingStart',
  WAITING_ROOM: 'waitingRoom',
};

export default {
  playSound,
  setGlobalVolume,
  preloadSounds,
  SOUNDS,
};
